/** The spine's five nodes (plan §1.1): each stage's key datum, and where the booking is in
 *  its life. Pure — `now` is a parameter.
 *
 *  Where "now" is: before the departure the booking is in its pre-check-in phase; from
 *  the departure to the trip's end it is on site; after the end it is in its after-trip
 *  phase. Booked is always past and Departure is an instant — past once it has happened,
 *  never current. The node before now is the current one (navy-filled); the "now" tick
 *  sits on the track right after it. Every time is in the tour's zone — the spine names
 *  the zone once. */
import type { BookingRecordEditContext } from '@/api/bookingRecord';
import type { BookingDetail } from '@/api/bookings';
import { tripPhase } from '@/features/records/time/tripPhase';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

import { SPINE_STAGES, type SpineStage } from '../registry';

export type SpineNodeState = 'past' | 'current' | 'future';

/** One node of a record's spine — the booking's five stages by default; the order's
 *  four (`features/orders/record/views/spineModel.ts`) fill the same shape. */
export interface SpineNode<S extends string = SpineStage> {
  stage: S;
  title: string;
  /** The stage's key datum ("Fri 2 Oct 09:30", "1 of 3 complete"). */
  datum: string;
  /** Its second line ("in 2 days", "Waivers 1 of 3"); null = none. */
  detail: string | null;
  state: SpineNodeState;
}

export interface SpineModel<S extends string = SpineStage> {
  nodes: SpineNode<S>[];
  /** Index of the current node — the "now" tick follows it. */
  current: number;
  /** The zone every time on the spine is in (the tour's; UTC for an order). */
  timezone: string;
}

export const STAGE_TITLES: Record<SpineStage, string> = {
  booked: 'Booked',
  precheckin: 'Pre-check-in',
  departure: 'Departure',
  onsite: 'On-site',
  after: 'After trip',
};

const CHECKIN_TEXT: Record<string, string> = {
  checked_in: 'Checked in',
  no_show: 'No-show',
  error: 'Check-in error',
  interactive: 'Interactive check-in',
  pending: 'Check-in pending',
  retry_later: 'Check-in retrying',
};

/** Group_Tour_User.STATE_BOOKED. */
const BOOKED = 1;

/** Group_Tour_User.PRECHECKIN_DETAILS_COMPLETED: every guest has saved details or is
 *  skipped, but the final waivers/consent screen was not submitted. The per-guest
 *  counts (checked in or skipped) cannot see it — saved details are not completion. */
const PRECHECKIN_DETAILS_COMPLETED = 3;

function instant(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function precheckinText(d: BookingDetail): { datum: string; detail: string | null } {
  const pc = d.precheckin;
  if (pc && !pc.enabled) return { datum: 'Off for this tour', detail: null };
  if (!pc) return { datum: d.precheckin_status_display || '—', detail: null };
  const total = Math.max(d.people_count ?? 0, pc.participants.length, 1);
  const done = pc.participants.filter((p) => p.checked_in || p.skipped).length;
  const datum =
    d.precheckin_status === PRECHECKIN_DETAILS_COMPLETED
      ? 'Details completed'
      : done === 0
        ? 'Not started'
        : done >= total
          ? 'Complete'
          : `${done} of ${total} complete`;
  const signed = d.waivers?.signed ?? 0;
  return { datum, detail: signed > 0 ? `Waivers ${signed} of ${total}` : null };
}

export function spineModel(
  { detail: d, editContext: ctx }: { detail: BookingDetail; editContext: BookingRecordEditContext },
  now: Date = new Date(),
): SpineModel {
  const tz = ctx.schedule.timezone;
  const short = (iso: string, opts: { weekday?: boolean; time?: boolean } = {}) =>
    formatZoned(iso, tz, { zone: false, ...opts, now });

  const startIso = ctx.schedule.start_ts ?? d.start_ts;
  const endIso = ctx.schedule.end_ts ?? d.end_ts;
  const t = now.getTime();
  // Pre-check-in until the departure, on site while the trip runs, then after the trip.
  const phase = tripPhase(startIso, endIso, now);
  const current = phase === 'active' ? 3 : phase === 'past' ? 4 : 1;

  const booked = {
    datum: d.booking_ts ? short(d.booking_ts, { weekday: false }) : 'Booking time not sent',
    detail:
      [d.order_ref ? `Order ${d.order_ref}` : 'No order', d.booking_state !== BOOKED ? d.booking_state_display : null]
        .filter(Boolean)
        .join(' · ') || null,
  };

  const departure = startIso
    ? {
        datum: d.all_day ? `${short(startIso, { time: false })} · all day` : short(startIso),
        detail: formatRelative(startIso, { now }),
      }
    : { datum: 'No departure time', detail: null };

  const status = d.checkin_status ?? 'unknown';
  let onsite: { datum: string; detail: string | null };
  if (status === 'unknown') {
    const w = ctx.checkin.window;
    const opens = instant(w.opens_ts);
    const closes = instant(w.closes_ts);
    onsite = {
      datum: 'Not checked in',
      detail: w.is_open
        ? 'Check-in list open'
        : opens !== null && opens > t
          ? `List opens ${short(w.opens_ts!)}`
          : closes !== null && closes <= t
            ? 'Check-in list closed'
            : null,
    };
  } else {
    const label = CHECKIN_TEXT[status] ?? status;
    onsite = {
      datum: status === 'checked_in' && d.checkin_ts ? `${label} ${short(d.checkin_ts, { weekday: false })}` : label,
      detail:
        status === 'error'
          ? d.checkin_error || null
          : d.checkin_source_display
            ? `via ${d.checkin_source_display}`
            : null,
    };
  }

  const after = {
    datum: endIso ? `Ends ${short(endIso)}` : 'No end time',
    detail: d.survey_state_display ? `Survey ${d.survey_state_display.toLowerCase()}` : null,
  };

  const content: Record<SpineStage, { datum: string; detail: string | null }> = {
    booked,
    precheckin: precheckinText(d),
    departure,
    onsite,
    after,
  };

  return {
    current,
    timezone: tz,
    nodes: SPINE_STAGES.map((stage, i) => ({
      stage,
      title: STAGE_TITLES[stage],
      ...content[stage],
      state: i < current ? 'past' : i === current ? 'current' : 'future',
    })),
  };
}
